// @ts-strict-ignore
import {
  Badge,
  Box,
  Button,
  Icon,
  IconName,
  Separator,
  Text,
  Tooltip,
  ValuePair,
} from '@usealloy/component-library';
import React from 'react';
import type { Document } from '~/entities/types';
import { DocumentRenderer } from './DocumentRenderer';
import { getVerifiedBadgeProps } from '../helpers/getVerifiedBadgeProps';
import type { SuppliedDataValue } from '../DocumentReviewModal';
import styled from 'styled-components';

// ============================================================
//     TYPES
// ============================================================
interface Props {
  document: Document;
  suppliedDataValues?: SuppliedDataValue[];
}

// ============================================================
//     STYLES
// ============================================================
const scrollbarStyles = `
  &::-webkit-scrollbar {
    -webkit-appearance: none;
  }
  &::-webkit-scrollbar:vertical {
    width: 8px;
    background-color: #f6f6f6;
    border-left: 1px solid #e0e2e6;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c3c3c3;
    border-radius: 8px;
  }
`;

const ScrollableBox = styled.div`
  max-height: calc(100vh - 420px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--cl-space-4);
  width: 100%;
  ${scrollbarStyles}
`;

const ScrollableDocumentColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--cl-space-2);
  background-color: var(--cl-gray-100);
  min-height: calc(var(--cl-space-5) + var(--cl-space-10) + var(--cl-space-5));
  max-height: calc(100vh - 420px);
  overflow-y: auto;
  ${scrollbarStyles}
`;

// ============================================================
//     COMPONENT
// ============================================================

export const DocReviewBody: React.FC<Props> = ({
  document,
  suppliedDataValues,
}) => {
  const hasSuppliedData = suppliedDataValues?.length > 0;
  // If suppliedData does not include any evaluated data, most likely we didn't check it against matched data so we should hide "Verified" column
  const showVerified = suppliedDataValues?.some(
    data => data.verified === true || data.verified === false,
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: hasSuppliedData ? 'minmax(min-content, 480px) minmax(400px, 1fr)' : '100%',
        gap: 'var(--cl-space-2)',
        width: '100%',
      }}
    >
      {/* LEFT COLUMN */}
      {hasSuppliedData && (
        <Box
          data-pendo-anchor="supplied-data-review-modal"
          direction="column"
          justifyContent="flex-start"
          style={{ padding: 'var(--cl-space-2)' }}
        >
          {/* DOC SUPPLIED DATA BOX */}
          <Box direction="column" gap={4}>
            <Box justifyContent="space-between">
              <Text weight={700}>
                Supplied Data
              </Text>
              {showVerified && (
              <Text weight={700}>
                Verified
              </Text>
              )}
            </Box>
            <Separator />

            <ScrollableBox>
              {suppliedDataValues.map(({ label, value, verified }) => {
                const { iconName, status, text } = getVerifiedBadgeProps(verified);

                return (
                  <Box key={`${label}-${value}`} justifyContent="space-between" gap={2}>
                    <ValuePair
                      isEditable={false}
                      dataType="string"
                      label={label}
                      value={value}
                    />
                    {showVerified && (
                    <Box alignItems="flex-end" justifyContent="flex-end">
                      <Badge
                        icon={iconName}
                        iconSize="small"
                        status={status}
                        style={{ borderRadius: 99, opacity: status === 'neutral' ? '.5' : undefined }}
                      >
                        {text}
                      </Badge>
                    </Box>
                    )}
                  </Box>
                );
              })}
            </ScrollableBox>
          </Box>
        </Box>
      )}
      {/* RIGHT COLUMN */}
      <ScrollableDocumentColumn>
        <Box direction="column" style={{ flexGrow: 1, position: 'relative' }}>
          <DocumentRenderer document={document} />
          {document.content_type?.includes('image/') ? (
            <Tooltip content="Download" side="top">
              <Button
                style={{
                  position: 'absolute',
                  bottom: 'var(--cl-space-4)',
                  right: 'var(--cl-space-4)',
                  padding: 'var(--cl-space-2)',
                }}
              >
                <a
                  href={document.url}
                  target="_blank"
                  className="secondary inline"
                  download={`${document.name}.${document.extension}`}
                  rel="noreferrer"
                >
                  <Icon icon={IconName.Download} />
                </a>
              </Button>
            </Tooltip>
          ) : null}
        </Box>
      </ScrollableDocumentColumn>
    </div>
  );
};
